import type { ExternalSqlProvider, LicenseEntitlements, LicenseType } from './productMode';
import type { TrustedTime } from './trustedTime';
import { unixSecondsFromIso } from './trustedTime';

const SQL_PROVIDERS: ExternalSqlProvider[] = ['postgresql', 'mysql', 'mssql', 'oracle', 'odbc'];

interface LicenseFileShape {
  format?: unknown;
  licenseId?: unknown;
  licenseeName?: unknown;
  authEmail?: unknown;
  licensedUserId?: unknown;
  licensedUserName?: unknown;
  qualifiedInvoiceNumber?: unknown;
  qualified_invoice_number?: unknown;
  licenseType?: unknown;
  standalone?: unknown;
  serverClient?: unknown;
  externalSqlProviders?: unknown;
  offlineGraceDays?: unknown;
  validFrom?: unknown;
  validUntil?: unknown;
  notBefore?: unknown;
  expiresAt?: unknown;
  licenseRequestId?: unknown;
  requestId?: unknown;
  deviceLockHash?: unknown;
  deviceLabel?: unknown;
  entitlements?: Partial<LicenseEntitlements>;
  payload?: unknown;
  signature?: {
    alg?: unknown;
    kid?: unknown;
    value?: unknown;
  };
}

const ISSUER_PUBLIC_KEY: JsonWebKey = {
  key_ops: ['verify'],
  ext: true,
  kty: 'EC',
  x: 'Sms1dw-5B6d5z66w14ZJ84-TZC7MvRtpCRu8NCzuS6g',
  y: 'cQ0ChSkxKp_wFp9rlQonXH3bO2F3hKvdbt2AK-nlFzE',
  crv: 'P-256',
};

function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const record = value as Record<string, unknown>;
  return `{${Object.keys(record)
    .sort()
    .map(key => `${JSON.stringify(key)}:${stableStringify(record[key])}`)
    .join(',')}}`;
}

function base64UrlToBuffer(value: string): ArrayBuffer {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(value.length / 4) * 4, '=');
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes.buffer;
}

async function verifyLicenseSignature(parsed: LicenseFileShape): Promise<void> {
  if (!parsed.payload || typeof parsed.payload !== 'object') {
    throw new Error('ライセンスファイルに署名対象ペイロードが含まれていません');
  }
  if (
    parsed.signature?.alg !== 'ES256' ||
    parsed.signature.kid !== 'revenue-prod-issuer-2026-01' ||
    typeof parsed.signature.value !== 'string'
  ) {
    throw new Error('ライセンスファイルの署名情報が正しくありません');
  }
  const publicKey = await crypto.subtle.importKey(
    'jwk',
    ISSUER_PUBLIC_KEY,
    { name: 'ECDSA', namedCurve: 'P-256' },
    false,
    ['verify'],
  );
  const valid = await crypto.subtle.verify(
    { name: 'ECDSA', hash: 'SHA-256' },
    publicKey,
    base64UrlToBuffer(parsed.signature.value),
    new TextEncoder().encode(stableStringify(parsed.payload)),
  );
  if (!valid) {
    throw new Error('ライセンスファイルの署名検証に失敗しました');
  }
}

function readString(value: unknown, label: string): string {
  if (typeof value !== 'string' || !value.trim()) {
    throw new Error(`${label}がライセンスファイルに含まれていません`);
  }
  return value.trim();
}

function readLicenseType(value: unknown): LicenseType {
  if (value === 'standalone' || value === 'server-client') return value;
  throw new Error('licenseTypeはstandaloneまたはserver-clientで指定してください');
}

function readSqlProviders(value: unknown): ExternalSqlProvider[] {
  if (!Array.isArray(value)) return [];
  return value.filter((provider): provider is ExternalSqlProvider =>
    SQL_PROVIDERS.includes(provider as ExternalSqlProvider)
  );
}

function readRequiredIsoDate(value: unknown, label: string): string {
  const dateText = readString(value, label);
  unixSecondsFromIso(dateText);
  return dateText;
}

function readOptionalIsoDate(value: unknown, label: string): string | null {
  if (value === null || value === undefined || value === '') return null;
  const dateText = readString(value, label);
  unixSecondsFromIso(dateText);
  return dateText;
}

function readOptionalString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  return text || null;
}

export function parseLicenseFileContent(content: string): LicenseEntitlements {
  let parsed: LicenseFileShape;
  try {
    parsed = JSON.parse(content) as LicenseFileShape;
  } catch {
    throw new Error('ライセンスファイルをJSONとして読み込めませんでした');
  }

  const source = (parsed.payload ?? parsed.entitlements ?? parsed) as LicenseFileShape & Partial<LicenseEntitlements>;
  const licenseType = readLicenseType(source.licenseType);
  const validFrom = readRequiredIsoDate(source.validFrom ?? source.notBefore, 'validFrom');
  const validUntil = readOptionalIsoDate(source.validUntil ?? source.expiresAt, 'validUntil');
  return {
    licenseId: readString(source.licenseId, 'licenseId'),
    licensedUserId: readString(source.licensedUserId, 'licensedUserId'),
    licensedUserName: readString(source.licensedUserName, 'licensedUserName'),
    qualifiedInvoiceNumber: readOptionalString(source.qualifiedInvoiceNumber ?? source.qualified_invoice_number),
    licenseType,
    standalone: source.standalone === undefined ? licenseType === 'standalone' : Boolean(source.standalone),
    serverClient: source.serverClient === undefined ? licenseType === 'server-client' : Boolean(source.serverClient),
    externalSqlProviders: readSqlProviders(source.externalSqlProviders),
    offlineGraceDays: Number.isFinite(source.offlineGraceDays) ? Number(source.offlineGraceDays) : 0,
    validFrom,
    validUntil,
    licenseRequestId: readOptionalString(source.licenseRequestId ?? source.requestId),
    deviceLockHash: readOptionalString(source.deviceLockHash),
    deviceLabel: readOptionalString(source.deviceLabel),
  };
}

export async function parseLicenseFile(file: File): Promise<LicenseEntitlements> {
  const content = await file.text();
  let parsed: LicenseFileShape;
  try {
    parsed = JSON.parse(content) as LicenseFileShape;
  } catch {
    throw new Error('ライセンスファイルをJSONとして読み込めませんでした');
  }
  await verifyLicenseSignature(parsed);
  return parseLicenseFileContent(content);
}

export function validateLicenseTimeWindow(entitlements: LicenseEntitlements, trustedTime: TrustedTime): void {
  const trustedUnixSeconds = trustedTime.unixSeconds;
  const validFrom = unixSecondsFromIso(entitlements.validFrom);
  if (trustedUnixSeconds < validFrom) {
    throw new Error(`ライセンスの開始日時前です。NTP時刻: ${trustedTime.server}`);
  }

  if (entitlements.validUntil) {
    const validUntil = unixSecondsFromIso(entitlements.validUntil);
    if (trustedUnixSeconds > validUntil) {
      throw new Error(`ライセンスの有効期限を過ぎています。NTP時刻: ${trustedTime.server}`);
    }
  }
}
